import { useDebounceFn } from '@vueuse/core';
import { computed, readonly, ref } from 'vue';

import type { SearchUsersData, User } from '@/shared/api';
import { Schema } from '@/shared/lib';

import { useUsersSearch } from '../../api';
import { UserInfoSchema, type UserInfoSchemaType } from '../../model';

const useUsersSearchController = () => {
  type SearchQuery = NonNullable<SearchUsersData['query']>['q'];

  const { usersSearch } = useUsersSearch();

  const searchQuery = ref<SearchQuery>('');
  const searchInputFocused = ref<boolean>(false);

  const users = ref<UserInfoSchemaType[]>([]);
  let searchVersion: number = 0;

  const isSearchQueryExists = computed<boolean>(() => !!searchQuery.value);

  const setUsers = (data: User[]): void => {
    users.value = data.map((el) => Schema.methods.parse(UserInfoSchema, el));
  };

  const search = useDebounceFn(async () => {
    const currentSearchVersion: number = searchVersion;
    const { isSuccess, data } = await usersSearch(searchQuery.value);

    if (isSuccess && currentSearchVersion === searchVersion) {
      setUsers(data ?? []);
    }
  }, 300);

  const onUpdateSearchInputFocused = (newVal: boolean): void => {
    searchInputFocused.value = newVal;
  };

  const onUpdateSearchQuery = (newVal: SearchQuery): void => {
    searchVersion += 1;
    searchQuery.value = newVal;

    if (newVal) {
      search();
    } else {
      users.value = [];
    }
  };

  return {
    users,
    searchInputFocused: readonly(searchInputFocused),
    isSearchQueryExists,
    onUpdateSearchInputFocused,
    onUpdateSearchQuery,
  };
};

export { useUsersSearchController };
